import { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "@/App";
import { Fingerprint, Mail, Lock, AlertCircle, ArrowLeft, Shield, Smartphone } from "lucide-react";

const LOGO_ICON = "https://static.prod-images.emergentagent.com/jobs/8bf96fc3-b1b9-4153-935e-e0d5c02560cb/images/b5ffd65d20497bfd9681416db86fdb9eea50ae3581cd0d0b7ff660d2e2271341.png";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // MFA states
  const [mfaRequired, setMfaRequired] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post("/auth/login", {
        email,
        password,
      });

      // Check if MFA is required
      if (response.data.mfa_required) {
        setMfaRequired(true);
        setTempToken(response.data.temp_token);
        setLoading(false);
        return;
      }

      // No MFA, proceed with login
      login(response.data.access_token, response.data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post("/auth/mfa/verify-login", {
        email,
        mfa_code: mfaCode,
        temp_token: tempToken,
      });

      login(response.data.access_token, response.data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setMfaRequired(false);
    setTempToken("");
    setMfaCode("");
    setError("");
  };

  return (
    <div className="min-h-screen bg-slate-900 flex relative">
      {/* Main Background Image */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />
      {/* Dark overlay */}
      <div className="fixed inset-0 z-0 bg-slate-900/85" />
      
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative z-10">
        <div>
          <Link to="/" className="inline-flex items-center space-x-2 text-blue-200 hover:text-white transition">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to home</span>
          </Link>
        </div>
        
        <div>
          <div className="flex items-center space-x-4 mb-8">
            <img src={LOGO_ICON} alt="FingerprintVS" className="w-16 h-16 rounded-xl" />
            <div>
              <h1 className="text-3xl font-heading font-bold text-white">FingerprintVS</h1>
              <p className="text-blue-200">Enterprise Biometric Solution</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Fingerprint className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <h3 className="text-white font-semibold">FBI Appendix F Compliant</h3>
                <p className="text-blue-200/70 text-sm">500 dpi resolution, NFIQ quality scoring</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <h3 className="text-white font-semibold">CJIS Security Policy v6.0</h3>
                <p className="text-blue-200/70 text-sm">End-to-end encryption, MFA support</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-blue-300/50 text-sm">
          © 2025 FingerprintVS. All rights reserved.
        </div>
      </div>
      
      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center space-x-3">
              <img src={LOGO_ICON} alt="FingerprintVS" className="w-12 h-12 rounded-lg" />
              <span className="text-2xl font-heading font-bold text-white">FingerprintVS</span>
            </Link>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8">
            {!mfaRequired ? (
              <>
                {/* Standard Login Form */}
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-heading font-bold text-white mb-2">
                    Welcome back
                  </h2>
                  <p className="text-blue-200/70">
                    Sign in to your account to continue
                  </p>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4 flex items-start space-x-3 mb-6">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-blue-100 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-blue-300/50" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-blue-200/40"
                        placeholder="you@example.com"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-blue-100 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-blue-300/50" />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-blue-200/40"
                        placeholder="••••••••"
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-3 px-4 text-base font-semibold rounded-lg text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg"
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      "Sign in"
                    )}
                  </button>
                </form>
                
                <div className="mt-6 text-center">
                  <p className="text-blue-200/70">
                    Don't have an account?{" "}
                    <Link
                      to="/register"
                      className="font-medium text-cyan-400 hover:text-cyan-300 transition"
                    >
                      Create one
                    </Link>
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* MFA Verification Form */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-8 h-8 text-cyan-400" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-white mb-2">
                    Two-Factor Authentication
                  </h2>
                  <p className="text-blue-200/70">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4 flex items-start space-x-3 mb-6">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleMfaVerify}>
                  <div>
                    <label htmlFor="mfaCode" className="block text-sm font-medium text-blue-100 mb-2">
                      Verification Code
                    </label>
                    <input
                      id="mfaCode"
                      type="text"
                      inputMode="numeric"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="block w-full px-4 py-4 bg-white/10 border border-white/20 text-white text-center text-3xl font-mono tracking-[0.5em] rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-blue-200/30"
                      data-testid="mfa-login-code-input"
                      autoFocus
                    />
                    <p className="mt-2 text-xs text-blue-200/60 text-center">
                      Or enter a backup code
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || mfaCode.length < 6}
                    className="w-full flex justify-center py-3 px-4 text-base font-semibold rounded-lg text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg"
                    data-testid="mfa-verify-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </span>
                    ) : (
                      "Verify & Sign in"
                    )}
                  </button>
                </form>
                
                <div className="mt-6 text-center">
                  <button
                    onClick={backToLogin}
                    className="text-blue-200/70 hover:text-white text-sm transition"
                  >
                    ← Back to login
                  </button>
                </div>
              </>
            )}
          </div>
          
          {/* Mobile Back Link */}
          <div className="lg:hidden mt-6 text-center">
            <Link to="/" className="text-blue-200/70 hover:text-white transition text-sm">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
